import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useSiteImage } from '@/hooks/useSiteImage';
import { IMAGES } from '@/constants/images';

interface GalleryImage {
  src: string;
  captionKey: string;
  wide: boolean;
}

// Fixed count (8), each key registered in SITE_IMAGE_KEYS — unrolled as
// individual useSiteImage() calls rather than a loop/`.map()` since hooks
// can't be called conditionally or inside callbacks.
const GALLERY_LENGTH = 8;

export default function LaunchGallerySection() {
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';

  const brandPanelUrl = useSiteImage('home.launchGallery.brandPanel', IMAGES.brandPanel);
  const boothUrl = useSiteImage('home.launchGallery.booth', IMAGES.booth);
  const consultUrl = useSiteImage('home.launchGallery.consult', IMAGES.consult);
  const crowdUrl = useSiteImage('home.launchGallery.crowd', IMAGES.crowd);
  const modelUrl = useSiteImage('home.launchGallery.model', IMAGES.model);
  const model2Url = useSiteImage('home.launchGallery.model2', IMAGES.model2);
  const teamUrl = useSiteImage('home.launchGallery.team', IMAGES.team);
  const team2Url = useSiteImage('home.launchGallery.team2', IMAGES.team2);

  const images: GalleryImage[] = [
    { src: brandPanelUrl, captionKey: 'home.launchGallery.caption.brandPanel', wide: true },
    { src: boothUrl, captionKey: 'home.launchGallery.caption.booth', wide: false },
    { src: consultUrl, captionKey: 'home.launchGallery.caption.consult', wide: false },
    { src: crowdUrl, captionKey: 'home.launchGallery.caption.crowd', wide: false },
    { src: modelUrl, captionKey: 'home.launchGallery.caption.model', wide: false },
    { src: model2Url, captionKey: 'home.launchGallery.caption.model2', wide: false },
    { src: teamUrl, captionKey: 'home.launchGallery.caption.team', wide: false },
    { src: team2Url, captionKey: 'home.launchGallery.caption.team2', wide: false },
  ];

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = useCallback((index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const goNext = useCallback(() => {
    setLightboxIndex((prev) => (prev + 1) % GALLERY_LENGTH);
  }, []);

  const goPrev = useCallback(() => {
    setLightboxIndex((prev) => (prev - 1 + GALLERY_LENGTH) % GALLERY_LENGTH);
  }, []);

  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowRight') {
        goNext();
      } else if (e.key === 'ArrowLeft') {
        goPrev();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [lightboxOpen, closeLightbox, goNext, goPrev]);

  const wideImage = images[0];
  const gridImages = images.slice(1);

  return (
    <section id="launch-gallery" className="w-full bg-white py-20 md:py-28 px-6 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        {/* === HEADER === */}
        <div className="flex flex-col items-center text-center mb-14 md:mb-20">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-px bg-[#7B1113]" />
            <span className="text-[11px] tracking-[0.2em] uppercase text-[#7B1113] font-medium">
              {t('home.launchGallery.eyebrow')}
            </span>
            <div className="w-8 h-px bg-[#7B1113]" />
          </div>

          {/* Heading */}
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#2A2A2A] leading-[1.15] max-w-3xl">
            {t('home.launchGallery.heading')}
          </h2>

          {/* Subline */}
          <p className="text-sm text-[#8A8A8A] font-light leading-[1.7] mt-5 max-w-2xl">
            {t('home.launchGallery.subline')}
          </p>
        </div>

        {/* === GALLERY GRID === */}
        <div className="flex flex-col gap-4">
          {/* Top wide image — brand panel */}
          <div
            className="relative w-full overflow-hidden rounded-xl cursor-pointer group"
            onClick={() => openLightbox(0)}
          >
            <div className="aspect-[21/9] w-full">
              <img
                src={wideImage.src}
                alt={t(wideImage.captionKey)}
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            {/* Dark overlay on hover */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors duration-400 flex items-end">
              <div className="w-full p-5 md:p-8 opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                <p className="text-white text-sm md:text-base font-serif italic drop-shadow-md">
                  {t(wideImage.captionKey)}
                </p>
              </div>
            </div>
          </div>

          {/* Grid of remaining images */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {gridImages.map((img, i) => {
              const realIndex = i + 1; // offset since index 0 is the wide image

              return (
                <div
                  key={img.src}
                  className="relative overflow-hidden rounded-xl cursor-pointer group aspect-[4/3]"
                  onClick={() => openLightbox(realIndex)}
                >
                  <img
                    src={img.src}
                    alt={t(img.captionKey)}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  {/* Dark overlay on hover */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-400 flex items-end">
                    <div className="w-full p-4 md:p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                      <p className="text-white text-xs md:text-sm font-serif italic drop-shadow-md leading-snug">
                        {t(img.captionKey)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* === BADGE === */}
        <div className="flex justify-center mt-10">
          <a
            href="https://www.instagram.com/p/DZ2LDIsk-Nx/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1A1A1A] text-white text-[11px] tracking-[0.15em] uppercase rounded-full hover:bg-[#333333] transition-colors cursor-pointer"
          >
            <i className="ri-tv-line text-sm"></i>
            <span>{t('home.launchGallery.badge')}</span>
            <i className="ri-arrow-right-up-line text-xs"></i>
          </a>
        </div>

        {/* === BOTTOM CTA === */}
        <div className="flex justify-center mt-20">
          <Link
            to={prefix + '/products'}
            className="px-9 py-4 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer font-medium whitespace-nowrap"
          >
            {t('home.launchGallery.cta')}
          </Link>
        </div>
      </div>

      {/* === LIGHTBOX === */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            className="absolute top-5 right-5 md:top-8 md:right-8 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer z-10"
            onClick={closeLightbox}
            aria-label="Close"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          {/* Previous button */}
          <button
            className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors cursor-pointer z-10"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            aria-label="Previous"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>

          {/* Image */}
          <div
            className="max-w-[90vw] max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[lightboxIndex].src}
              alt={t(images[lightboxIndex].captionKey)}
              className="max-w-full max-h-[75vh] object-contain rounded-lg"
            />
            {/* Caption */}
            <p className="text-white/80 text-sm md:text-base font-serif italic mt-4 text-center px-4">
              {t(images[lightboxIndex].captionKey)}
            </p>
            {/* Counter */}
            <span className="text-white/40 text-xs mt-2 font-light tracking-wider">
              {lightboxIndex + 1} / {images.length}
            </span>
          </div>

          {/* Next button */}
          <button
            className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors cursor-pointer z-10"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            aria-label="Next"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>
        </div>
      )}
    </section>
  );
}